import { isLoggedIn, isUserSales, request } from "./utils";
import { withoutShadow } from "./shadowSession";

/**
 * Refresh whoever the active token belongs to.
 *
 * `intoOwnUser` forces the answer into `user` rather than deciding from
 * `impersonatingUser`. The two can disagree for a moment — a shadow session
 * that has just ended has the admin's token back but a render still to go —
 * and writing the shadowed user into `user` on that render replaced the admin
 * with them for the rest of the session. Only a full page reload put it right.
 */
async function reloadUserData(store: any, setStore: any, intoOwnUser = false) {
  if (isLoggedIn(store)) {
    const result: any = await request(store, "users/data/", "GET");
    if (result.ok) {
      const data = result?.data;
      const updatedStore = { ...store };

      if (!intoOwnUser && store?.impersonatingUser) {
        // The token is the shadowed user's, so this response *is* them. The
        // admin doing the shadowing stays put in `user`, untouched.
        updatedStore.impersonatingUser = data?.user ?? store.impersonatingUser;
      } else {
        updatedStore.user = data?.user;
      }
      updatedStore.notifications = data?.notifications;
      updatedStore.agency_settings = data?.agency_settings;

      setStore(updatedStore);
      return updatedStore;
    }
  }
}

/**
 * Start a shadow session.
 *
 * The API mints a short lived token naming both parties (`sub` = the user being
 * shadowed, `act` = the admin doing it). Swapping the active token for it is what
 * makes server-side scoping work: every request now arrives as that user, so no
 * page has to pass their id. The admin's own token is kept aside for the way out.
 */
async function onShadowUser(store: any, setStore: any, user_id: number, navigate: any) {
  const result = await request(store, "users/" + user_id, "GET");
  if (!result.ok) return;

  const target: any = result.data;
  const shadow: any = await request(store, "shadow/" + encodeURIComponent(target?.email), "POST");
  if (!shadow.ok || !shadow?.data?.access_token) return;

  const updatedStore = {
    ...store,
    impersonatingUser: target,
    access_token: shadow.data.access_token,
    // Millisecond deadline, so the badge can end the session before the token
    // expires and requests start coming back 401.
    shadow_expires_at: Date.now() + (shadow.data.expires_in_minutes ?? 15) * 60 * 1000,
    // Kept so stopping the session can put the admin back without a re-login.
    admin_access_token: store.admin_access_token ?? store.access_token
  };
  setStore(updatedStore);

  if (isUserSales(updatedStore)) {
    navigate("/sales-dashboard");
  } else {
    navigate("/dashboard");
  }
}

/**
 * Put the admin's own token back and drop the shadowed user.
 *
 * The admin's own record is fetched again afterwards rather than trusted from
 * the store. It could have been overwritten with the shadowed user's details
 * by any refresh that landed during the session, and the header then keeps
 * showing the wrong person until the page is reloaded by hand.
 */
async function stopShadowingUser(store: any, setStore: any) {
  const updatedStore = withoutShadow(store);
  setStore(updatedStore);
  await reloadUserData(updatedStore, setStore, true);
  return updatedStore;
}

/**
 * Sign out, ending any shadow session on the way.
 *
 * The store is dropped entirely, so the shadow keys go with it — but they are
 * cleared first so nothing that reads the store between here and the redirect
 * still believes a shadow session is running.
 */
function logoutUser(store: any, setStore: any) {
  if (store?.impersonatingUser) {
    setStore(withoutShadow(store));
  }
  setStore(null);
  localStorage.removeItem("data");
}

export { reloadUserData, onShadowUser, stopShadowingUser, logoutUser };
